import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useServerStore } from '../../store/serverStore';
import { X, Folder, Hash, Volume2, Lock } from 'lucide-react';

interface CreateChannelModalProps {
  onClose: () => void;
  initialType?: 'text' | 'voice' | 'category';
  initialParentId?: string | null;
}

const CreateChannelModal: React.FC<CreateChannelModalProps> = ({ onClose, initialType = 'text', initialParentId = null }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'text' | 'voice' | 'category'>(initialType);
  const [isPrivate, setIsPrivate] = useState(false);
  const [parentId, setParentId] = useState(initialParentId || '');
  const { createChannel, currentServer, channels, isLoading, error } = useServerStore();
  const categories = channels
    .filter((channel: any) => channel.type === 'category')
    .sort((a: any, b: any) => (a.position || 0) - (b.position || 0));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentServer) return;

    const channel = await createChannel(
      name,
      currentServer.id,
      type,
      type === 'category' ? false : isPrivate,
      description || null,
      type === 'category' ? null : parentId || null,
    );

    if (channel) onClose();
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-border-color bg-bg-secondary shadow-2xl shadow-black/50 animate-slide-up">
        <header className="flex items-center justify-between border-b border-border-color px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-text-primary">Create {type === 'category' ? 'Category' : 'Channel'}</h2>
            <p className="mt-0.5 text-xs text-text-muted">Organize your server with Discord-style categories and channels.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-text-muted hover:bg-highlight hover:text-text-primary transition-colors">
            <X className="h-5 w-5" />
          </button>
        </header>

        {error && (
          <div className="bg-error/20 border border-error text-error p-3 rounded-md mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 p-5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.08em] text-text-muted mb-2">Type</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'text', label: 'Text', icon: Hash },
                { id: 'voice', label: 'Voice', icon: Volume2 },
                { id: 'category', label: 'Category', icon: Folder },
              ].map((item) => {
                const Icon = item.icon;
                const selected = type === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`flex min-h-[78px] flex-col items-center justify-center rounded-xl border px-3 py-3 text-sm font-semibold transition-all ${
                      selected
                        ? 'border-accent-color bg-accent-color/15 text-text-primary shadow-lg shadow-accent-color/10'
                        : 'border-border-color bg-bg-tertiary/60 text-text-secondary hover:border-accent-color/40 hover:text-text-primary'
                    }`}
                    onClick={() => setType(item.id as typeof type)}
                  >
                    <Icon className={selected ? 'mb-2 h-5 w-5 text-accent-color' : 'mb-2 h-5 w-5 text-text-muted'} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="channel-name" className="block text-sm font-medium text-text-secondary mb-1">
              Name <span className="text-error">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {type === 'category'
                  ? <Folder className="h-4 w-4 text-text-muted" />
                  : type === 'text'
                    ? <Hash className="h-4 w-4 text-text-muted" />
                    : <Volume2 className="h-4 w-4 text-text-muted" />}
              </div>
              <input
                id="channel-name"
                type="text"
                value={name}
                onChange={(e) => setName(type === 'category' ? e.target.value : e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                required
                className="settings-input w-full !pl-10"
                placeholder={type === 'category' ? 'General' : type === 'text' ? 'new-channel' : 'Voice Channel'}
              />
            </div>
          </div>

          {type !== 'category' && categories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Category</label>
              <select value={parentId} onChange={(event) => setParentId(event.target.value)} className="settings-input w-full">
                <option value="">No category</option>
                {categories.map((category: any) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="channel-description" className="block text-sm font-medium text-text-secondary mb-1">
              Description <span className="text-text-muted">(optional)</span>
            </label>
            <textarea
              id="channel-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="settings-input w-full"
              placeholder={type === 'category' ? 'What belongs here?' : "What's this channel about?"}
            />
          </div>

          {type !== 'category' && (
            <div className="flex items-center">
              <input
                id="private-channel"
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="h-4 w-4 text-accent-color focus:ring-accent-color border-border-color rounded bg-bg-tertiary"
              />
              <label htmlFor="private-channel" className="ml-2 flex items-center text-sm text-text-secondary">
                <Lock className="h-4 w-4 mr-1" />
                Private Channel
              </label>
            </div>
          )}

          {type !== 'category' && isPrivate && (
            <div className="bg-bg-tertiary p-3 rounded-md text-sm text-text-secondary">
              <p>This channel will only be visible to selected members and roles.</p>
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-border-color pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button type="submit" disabled={isLoading || !name.trim()} className="btn btn-primary">
              {isLoading ? 'Creating...' : `Create ${type === 'category' ? 'Category' : 'Channel'}`}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default CreateChannelModal;
